import type { SupermarketSection } from '@cadencia/contracts';
import { csvField } from '../export/csv.js';
import { groupBySection, shoppingProgress, type ShoppingLine } from './list.js';

/**
 * Exportar y compartir la lista (docs/NUTRITION_ENGINE.md §12.3, CA-5.5).
 *
 * Dos formatos, y cada uno para una cosa distinta: el texto plano es para
 * pegarlo en un mensaje y leerlo con el movil en la mano; el CSV es para abrirlo
 * en una hoja de calculo. Ninguno de los dos mezcla `needed` con `purchase`.
 */

/** Nombre de cada seccion. Unica fuente: lo usan el export y la pantalla. */
export const SECTION_LABEL: Record<SupermarketSection, string> = {
  produce: 'Frutas y verduras',
  meat: 'Carne',
  fish: 'Pescado',
  eggs: 'Huevos',
  dairy: 'Lácteos',
  cereals: 'Cereales',
  legumes: 'Legumbres',
  canned: 'Conservas',
  frozen: 'Congelados',
  other: 'Otros',
};

/**
 * Una cantidad tal y como se escribe en castellano.
 *
 * `${cantidad}` a secas usa el separador de JavaScript, que es el punto: en la
 * lista de la compra salia «necesitas 728.4 g» en una aplicacion que es solo en
 * castellano y que dos lineas mas arriba escribe «0 %» y «2 x 500 g» bien. Es
 * el mismo fallo que el presupuesto de la IA, que se pintaba «5,000 €».
 *
 * No se usa `toLocaleString`: el dominio es determinista por ADR-007 y el
 * formato de un `Intl` depende de los datos de ICU que traiga el runtime. Un
 * reemplazo explicito da siempre lo mismo.
 *
 * `toCsv` NO pasa por aqui, y es a proposito: alli las cifras van crudas, que
 * es lo que una hoja de calculo sabe leer, y ademas una coma decimal dentro de
 * un fichero separado por comas seria una bomba.
 */
function conComa(cantidad: number): string {
  return String(cantidad).replace('.', ',');
}

/** Unidad como se lee, no como se guarda. */
function unidad(cantidad: number, unit: string): string {
  if (unit !== 'unit') return `${conComa(cantidad)} ${unit}`;
  return cantidad === 1 ? '1 unidad' : `${conComa(cantidad)} unidades`;
}

/**
 * Como se compra una linea, en una frase.
 *
 * Con formato comercial se dice el formato («2 × 500 g»), porque en la
 * estanteria no hay 870 g: hay paquetes. Sin formato, la cantidad redondeada.
 */
export function purchaseText(line: ShoppingLine): string {
  if (line.purchaseAmount === 0) return 'ya lo tienes';
  if (line.packSize !== null && line.packCount !== null) {
    return `${line.packCount} × ${unidad(line.packSize, line.purchaseUnit)}`;
  }
  return unidad(line.purchaseAmount, line.purchaseUnit);
}

/** Lo que pide la nutricion, con la resta de despensa si la hubo. */
export function neededText(line: ShoppingLine): string {
  const base = `necesitas ${unidad(line.neededAmount, line.neededUnit)}`;
  if (line.pantryDeducted <= 0) return base;
  return `${base} · tienes ${unidad(line.pantryDeducted, line.neededUnit)}`;
}

export interface ExportInput {
  lines: readonly (ShoppingLine & { isChecked: boolean })[];
  rangeStart: string;
  rangeEnd: string;
}

/**
 * Texto plano para compartir.
 *
 * Las secciones van en mayusculas, como en la pantalla, para que el bloque se
 * recorra de un vistazo mientras se empuja el carro.
 */
export function toPlainText(input: ExportInput): string {
  const progreso = shoppingProgress(input.lines);
  const partes: string[] = [
    'LISTA DE LA COMPRA',
    `${input.rangeStart} a ${input.rangeEnd}`,
    `${progreso.checked} de ${progreso.total} ítems · ${progreso.pct} %`,
  ];

  for (const grupo of groupBySection(input.lines)) {
    partes.push('', SECTION_LABEL[grupo.section].toUpperCase());
    for (const line of grupo.lines) {
      const marca = line.isChecked ? '[x]' : '[ ]';
      partes.push(`${marca} ${line.nameEs} — ${neededText(line)} · compra ${purchaseText(line)}`);
    }
  }

  return partes.join('\n');
}

/**
 * CSV para hoja de calculo.
 *
 * `needed` y `purchase` son columnas distintas, igual que en la tabla: quien
 * abra esto tiene que poder sumar lo que compro y compararlo con lo que
 * necesitaba, y eso es imposible si el fichero trae un solo numero.
 */
export function toCsv(input: ExportInput): string {
  const cabecera = [
    'seccion',
    'alimento',
    'necesario',
    'unidad_necesaria',
    'despensa_restada',
    'compra',
    'unidad_compra',
    'formato',
    'paquetes',
    'marcado',
  ];

  const filas = input.lines.map((line) =>
    [
      SECTION_LABEL[line.section],
      line.nameEs,
      line.neededAmount,
      line.neededUnit,
      line.pantryDeducted,
      line.purchaseAmount,
      line.purchaseUnit,
      line.packSize ?? '',
      line.packCount ?? '',
      line.isChecked ? 'si' : 'no',
    ]
      .map(csvField)
      .join(','),
  );

  return [cabecera.join(','), ...filas].join('\n');
}
